import React from "react";
import { Link } from "react-router-dom";
import { Search, X } from "lucide-react";
import { useSearch } from "@/shared/contexts";
import { cn } from "@/shared/utils";

interface SearchResultItem {
  id: number;
  title: string;
  path: string;
  type: "page" | "app";
}

interface SearchResultsProps {
  className?: string;
}

export const SearchResults: React.FC<SearchResultsProps> = ({ className }) => {
  const { searchResults, clearSearch, searchQuery } = useSearch();

  // Show results even if searchQuery is empty, but only if we have results
  if (searchResults.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        "absolute top-full left-0 right-0 mt-2 bg-slate-800/95 backdrop-blur-xl border border-slate-600/50 rounded-xl shadow-2xl shadow-slate-900/50 z-50",
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-600/30">
        <div className="flex items-center space-x-2">
          <Search className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-medium text-slate-300">
            {searchResults.length} result{searchResults.length !== 1 ? "s" : ""} for "{searchQuery}"
          </span>
        </div>
        <button
          onClick={clearSearch}
          className="p-1 rounded-lg hover:bg-slate-700/50 transition-colors duration-200"
        >
          <X className="w-4 h-4 text-slate-400 hover:text-white" />
        </button>
      </div>

      {/* Results */}
      <div className="max-h-80 overflow-y-auto">
        {searchResults.map((result: SearchResultItem) => (
          <Link
            key={result.id}
            to={result.path}
            onClick={clearSearch}
            className="flex items-center space-x-3 p-3 hover:bg-slate-700/30 transition-colors duration-200 group"
          >
            {/* Icon */}
            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-slate-600/30 to-slate-500/20 flex items-center justify-center">
              <Search className="w-4 h-4 text-slate-300 group-hover:text-white transition-colors duration-200" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-200 group-hover:text-white transition-colors duration-200">
                {result.title}
              </div>
              <div className="text-xs text-slate-400 group-hover:text-slate-300 transition-colors duration-200">
                {result.path}
              </div>
            </div>

            {/* Type Badge */}
            <div
              className={cn(
                "px-2 py-1 rounded-md text-xs font-medium",
                result.type === "app"
                  ? "bg-blue-500/20 text-blue-300"
                  : "bg-slate-500/20 text-slate-300",
              )}
            >
              {result.type}
            </div>
          </Link>
        ))}
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-slate-600/30">
        <div className="text-xs text-slate-400 text-center">
          Press <kbd className="px-1.5 py-0.5 bg-slate-700/50 rounded text-slate-300">Esc</kbd> to
          close
        </div>
      </div>
    </div>
  );
};
